import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SectionHeading } from "../SectionHeading";
import { Reveal } from "../Reveal";
import { PROCESS } from "../../data/content";
import { useLang } from "../../i18n/LanguageContext";

export const Process = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 70%"] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="process" data-testid="process-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading overline={t(PROCESS.overline)} title={t(PROCESS.title)} />
        <div ref={ref} className="relative mt-16 pl-8 md:pl-0">
          {/* vertical rail */}
          <div className="absolute left-[7px] md:left-1/2 top-2 bottom-2 w-px bg-white/10 md:-translate-x-1/2" />
          <motion.div
            style={{ scaleY: lineScale }}
            className="absolute left-[7px] md:left-1/2 top-2 bottom-2 w-px origin-top bg-gradient-to-b from-[#60A5FA] via-[#3B82F6] to-[#F59E0B] md:-translate-x-1/2"
          />
          <div className="space-y-10 md:space-y-0">
            {PROCESS.steps.map((step, i) => {
              const left = i % 2 === 0;
              return (
                <Reveal key={i} delay={0.05}>
                  <div className={`relative md:grid md:grid-cols-2 md:gap-16 ${left ? "" : "md:[direction:rtl]"} md:py-8`}>
                    {/* node */}
                    <span className="absolute left-[-25px] md:left-1/2 top-1.5 md:top-9 h-3.5 w-3.5 -translate-x-0 md:-translate-x-1/2 rounded-full bg-[#60A5FA] shadow-[0_0_16px_rgba(96,165,250,0.8)]" />
                    <div className={`[direction:ltr] ${left ? "md:text-right md:pr-4" : "md:col-start-2 md:pl-4"}`} data-testid={`process-step-${i}`}>
                      <span className="font-mono text-sm text-[#F59E0B]">{step.n}</span>
                      <h3 className="mt-2 text-2xl font-medium tracking-tight text-white">{t(step.name)}</h3>
                      <p className="mt-2 text-white/50 leading-relaxed max-w-sm md:inline-block">{t(step.desc)}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
        <Reveal delay={0.1}>
          <div className="mt-16 flex justify-center md:justify-start">
            <Link
              to="/contact"
              data-testid="process-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#3B82F6] px-7 py-3.5 text-sm font-medium text-white transition-all duration-300 shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:shadow-[0_0_45px_rgba(59,130,246,0.7)] hover:bg-[#60A5FA] active:scale-[0.98]"
            >
              {t(PROCESS.cta)}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
